import Button from 'components/Form/Button';
import { CoreContext } from 'context/CoreContext';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { EproposalConfirm } from 'screens/EproposalConfirm';
import { EProposalInvalidToken } from 'screens/EProposalInvalidToken';
import { GET, POST, ReadAddressesByZipCode } from 'services/api';
import { getProfissionalClasses } from 'services/api-omni';
import { getTodos } from 'services/produtos';
import { Title } from 'ui/styled';
import { ThemedComponent } from 'ui/theme';
import { exposeStrapiError, parseStrapiImage } from 'utils';
import {
  esadoCivilOptions,
  genderOptions,
  nacionalidadeOptions,
  optionsOrgaoEmissorRg,
  optionsStates,
  situacOptions,
} from 'utils/options';
import { formatPhone, unmaskPhone } from 'utils/parsers';
import { normalizeName } from 'utils/validators';
import FormCore from '../../components/Form/Core';
import {
  ButtonContainer,
  FormSpacing,
  HeaderButtonContainer,
} from '../FinancingProposalNew/styled';
import { EproposalContainer, EproposalFormsWrapper } from './styled';

export default function Eproposal() {
  const { token } = useParams();
  const { setModal } = useContext(CoreContext);
  const refForm = useRef();
  const refFormInstallationAddress = useRef();
  const refFormCliente = useRef();
  const refFormProfessional = useRef();
  const [loading, setLoading] = useState(true);
  const [isBanking, setIsBanking] = useState(false);
  const [professionalClasses, setProfessionalClasses] = useState([]);
  const [marcasMod, setMarcasMod] = useState([]);
  const [marcasInv, setMarcasInv] = useState([]);
  const [tipoCliente, setTipoCliente] = useState('fisica');
  const [professionalDescription, setProfessionalDescription] = useState();
  const [professionalData, setProfessionalData] = useState({});
  const [distributorId, setDistributorId] = useState();
  const [confirmDisplay, setConfirmDisplay] = useState(false);
  const [logoURL, setLogoURL] = useState('');
  const [isBankingData, setIsBankingData] = useState({});

  const isMobile = window.matchMedia('(max-width: 768px)').matches;

  const saveUrl = '/e-proposal/createlp';

  const LOCALHOST = process.env.REACT_APP_API_URL;

  const pessoFisJudi = [
    { id: 'fisica', title: 'Fisica' },
    { id: 'juridica', title: 'Juridica' },
  ];

  const handleGetModelsAndInverters = useCallback(async () => {
    const result = await getTodos();
    if (result && !exposeStrapiError(result)) {
      const modules = [];
      const inverters = [];

      for (var i = 0; i < result?.data.length; i++) {
        const item = result.data[i];
        if (item.attributes.ativo) {
          if (item.attributes.tipo === 'Inversor') {
            inverters.push({
              id: item.attributes.marca,
              title: item.attributes.marca,
              name: item.attributes.marca,
            });
          } else {
            modules.push({
              id: item.attributes.marca,
              title: item.attributes.marca,
              name: item.attributes.marca,
            });
          }
        }
      }
      setMarcasMod(modules.sort((a, b) => a.title.localeCompare(b.title)));
      setMarcasInv(inverters.sort((a, b) => a.title.localeCompare(b.title)));
    }
  }, []);

  const handleBuscaPreData = async (token) => {
    setLoading(true);
    const url = `/pre-proposals/getpretoken/${token}`;

    try {
      const res = await GET(url);
      if (res?.error && res?.error?.status === 400) {
        setConfirmDisplay('400');
      }
      //console.log(res);

      return res;
    } catch (err) {
      console.log('ServerParseError', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInitialForm = useCallback(async () => {
    await handleGetModelsAndInverters();

    const data = await handleBuscaPreData(token);

    const svgPath = data?.distribuidor?.logo_svg?.url ?? undefined;
    if (!!svgPath && !logoURL) {
      const svgURL = parseStrapiImage(`${LOCALHOST}${svgPath}`);
      setLogoURL(svgURL);
    }

    // Id de distribuidor vindo da pré-proposta, caso exista
    if (data?.distribuidor?.id) setDistributorId(data?.distribuidor?.id);

    if (!!data?.isBanking) {
      setIsBanking(data.isBanking);
      try {
        const profissionalClasses = await getProfissionalClasses({
          distributorId: data?.distribuidor?.id,
        });

        setProfessionalClasses(profissionalClasses?.classesProfissionais);

        const isBankingPreData = {
          cnpj_empresa: data?.informacoes_profissionais?.cnpj_empresa,
          nome_empresa: data?.informacoes_profissionais?.nome_empresa,
          telefone_empresa: data?.informacoes_profissionais?.telefone_empresa,
          tempo_empresa: data?.informacoes_profissionais?.tempo_empresa,
          renda_mensal: data?.informacoes_profissionais?.renda_mensal,
          patrimonio_estimado:
            data?.informacoes_profissionais?.patrimonio_estimado,
          situacao_imovel: data?.situacao_imovel,
          ocupacao: data?.informacoes_profissionais?.ocupacao_code,
          ocupacao_label: data?.informacoes_profissionais?.ocupacao,
          profissao: data?.informacoes_profissionais?.profissao_code,
          profissao_label: data?.informacoes_profissionais?.profissao,
        };
        setIsBankingData(isBankingPreData);
      } catch (err) {
        console.error('Erro Omni:', err);
      }
    }

    // === 1. FORMULÁRIO DO CLIENTE ===
    const cpfCnpj = data?.cpf || data?.cnpj || '';
    const tipoPessoa =
      data?.tipo_cliente === 'fisica' ||
      cpfCnpj.replace(/\D/g, '').length === 11
        ? 'fisica'
        : 'juridica';
    refFormCliente?.current?.setItem(tipoPessoa, 'tcc2');
    refFormCliente?.current?.setItem(
      data?.nome_completo?.trim() || '',
      'nome_completo'
    );
    refFormCliente?.current?.setItem(cpfCnpj, 'cpf');
    refFormCliente?.current?.setItem(data?.email || '', 'email');
    refFormCliente?.current?.setItem(
      formatPhone('', data?.celular),
      'telefone_celular'
    );
    refFormCliente?.current?.setItem(
      data?.data_nascimento || '',
      'data_nascimento'
    );

    refFormCliente?.current.setItem(
      data?.informacoes_cliente?.rg_emissor || '',
      'rg_emissor'
    );
    refFormCliente?.current.setItem(
      data?.informacoes_cliente?.rg_emissor_uf || '',
      'rg_emissor_uf'
    );
    refFormCliente?.current.setItem(
      data?.informacoes_cliente?.rg_data_emissao || '',
      'rg_data_emissao'
    );

    // Endereço residencial do cliente
    refFormCliente?.current?.setItem(data?.cep || '', 'cep_cliente');
    refFormCliente?.current?.setItem(
      data?.logradouro || '',
      'logradouro_cliente'
    );
    refFormCliente?.current?.setItem(data?.numero || '', 'numero_cliente');
    refFormCliente?.current?.setItem(data?.complemento || '', 'complemento');
    refFormCliente?.current?.setItem(data?.bairro || '', 'bairro');
    refFormCliente?.current?.setItem(data?.cidade || '', 'cidade');
    refFormCliente?.current?.setItem(data?.uf || '', 'estado');

    refFormCliente?.current?.setItem(data?.id, 'id_pre_proposta');

    // === 2. ENDEREÇO DE INSTALAÇÃO (mesmo do cliente na pré-proposta) ===
    refFormInstallationAddress?.current?.setItem(data?.cep || '', 'cep');
    refFormInstallationAddress?.current?.setItem(data?.logradouro || '', 'rua');
    refFormInstallationAddress?.current?.setItem(data?.numero || '', 'numero');
    refFormInstallationAddress?.current?.setItem(
      data?.complemento || '',
      'complemento'
    );
    refFormInstallationAddress?.current?.setItem(data?.bairro || '', 'bairro');
    refFormInstallationAddress?.current?.setItem(data?.cidade || '', 'cidade');
    refFormInstallationAddress?.current?.setItem(data?.uf || '', 'estado');
    // === 3. INFORMAÇÕES PROFISSIONAIS ===
    if (data?.renda_mensal && refFormProfessional?.current) {
      refFormProfessional.current.setItem(data.renda_mensal, 'renda_mensal');
    }
    // === 4. DADOS DO PROJETO (principais) ===
    if (refForm?.current) {
      // Marca como vindo da pré-proposta
      // refForm.current.setItem(`${data.id}`, 'cod_proposta_externa');
      // Valor total do financiamento = custo do projeto
      const valor = data?.valor_financiamento || 0;
      refForm.current.setItem(valor, 'custo_produto');
      refForm.current.setItem(valor, 'custo_total_projeto');
      refForm.current.setItem(data?.cnpj_integrador || '', 'cnpj_integrador');
      refForm.current.setItem(
        formatPhone('', data?.telefone_vendedor),
        'telefone_vendedor'
      );
      refForm.current.setItem(data?.nome_vendedor || '', 'nome_vendedor');
    }
  }, [handleGetModelsAndInverters, token, LOCALHOST, logoURL]);

  const handleTotalValue = useCallback(() => {
    let custProd = refForm?.current?.getItem('custo_produto') || 0;
    let custServ = refForm?.current?.getItem('custo_servico') || 0;
    let custOutros = refForm?.current?.getItem('valor_outros_produtos') || 0;
    refForm?.current?.setItem(
      custProd + custServ + custOutros,
      'custo_total_projeto'
    );
  }, []);

  const handleGetAddressByCep = useCallback(async () => {
    const cep = refFormCliente?.current
      ?.getItem('cep_cliente')
      .replace(/\D/g, '');
    if (cep.length === 8) {
      try {
        const data = await ReadAddressesByZipCode(cep);
        if (data) {
          refFormCliente?.current?.setItem(
            data.logradouro,
            'logradouro_cliente'
          );
          refFormCliente?.current?.setItem(data.bairro, 'bairro');
          refFormCliente?.current?.setItem(data.localidade, 'cidade');
          refFormCliente?.current?.setItem(data.uf, 'estado');
          refFormInstallationAddress?.current?.setItem(data.cep, 'cep');
          refFormInstallationAddress?.current?.setItem(data.logradouro, 'rua');
          refFormInstallationAddress?.current?.setItem(data.bairro, 'bairro');
          refFormInstallationAddress?.current?.setItem(
            data.localidade,
            'cidade'
          );
          refFormInstallationAddress?.current?.setItem(data.uf, 'estado');
          refFormInstallationAddress?.current?.setItem(
            refFormCliente?.current?.getItem('numero_cliente'),
            'numero'
          );
        }
      } catch (err) {
        console.warn('Erro ao buscar CEP:', err);
        toast.warn('CEP não encontrado.');
      }
    }
  }, []);

  const handleInstallationAddress = useCallback(async (event) => {
    const cep = event.target.value.replace(/\D/g, '');
    if (cep.length === 8) {
      try {
        const data = await ReadAddressesByZipCode(cep);
        if (data) {
          refFormInstallationAddress?.current?.setItem(data?.logradouro, 'rua');
          refFormInstallationAddress?.current?.setItem(data?.bairro, 'bairro');
          refFormInstallationAddress?.current?.setItem(
            data.localidade,
            'cidade'
          );
          refFormInstallationAddress?.current?.setItem(data?.uf, 'estado');
          refFormInstallationAddress?.current?.setItem('', 'numero');
          refFormInstallationAddress?.current?.setItem('', 'complemento');
        }
      } catch (err) {
        console.warn('Erro ao buscar CEP:', err);
        toast.warn('CEP não encontrado.');
      }
    }
  }, []);

  useEffect(() => handleInitialForm(), [handleInitialForm]);

  // Função para calcular potência em kWp
  const handleCalculoPotencia = useCallback(() => {
    setTimeout(() => {
      const qtde = Number(refForm.current?.getItem('quantidade_placas') || 0);
      const watts = Number(refForm.current?.getItem('potencia_modulo') || 0);
      const kwp = (qtde * watts) / 1000;
      refForm.current?.setItem(kwp.toFixed(2), 'potencia_kWp');
    }, 100);
  }, []);

  const classesOptions = useMemo(() => {
    return (
      professionalClasses?.map((item) => ({
        id: item.codigo,
        title: item.descricao,
      })) || []
    );
  }, [professionalClasses]);

  const formProjectItems = [
    {
      ref: 'cnpj_integrador',
      placeholder: 'CNPJ Integrador',
      mask: '99.999.999/9999-99',
      tress: true,
      required: true,
    },
    {
      ref: 'telefone_integrador',
      placeholder: 'Telefone Integrador',
      mask: '(99)99999-9999',
      disabled: false,
      tress: true,
      required: true,
      Tooltip: 'Preencha com números válidos — indispensável para comunicação.',
    },
    {
      ref: 'nome_vendedor',
      placeholder: 'Nome Completo do Vendedor',
      tress: true,
      required: true,
    },
    {
      ref: 'telefone_vendedor',
      placeholder: 'Telefone do Vendedor',
      mask: '(99)99999-9999',
      disabled: false,
      tress: true,
      required: true,
      Tooltip: 'Preencha com números válidos — indispensável para comunicação.',
    },
    {
      ref: 'custo_produto',
      type: 'money',
      placeholder: 'Valor dos Produtos',
      tress: true,
      required: true,
      onBlur: handleTotalValue,
    },
    {
      ref: 'custo_servico',
      type: 'money',
      placeholder: 'Valor do Serviço',
      tress: true,
      required: false,
      onBlur: handleTotalValue,
    },
    {
      ref: 'valor_outros_produtos',
      type: 'money',
      placeholder: 'Valor Outros',
      required: false,
      default: 0,
      tress: true,
      onBlur: handleTotalValue,
    },
    {
      ref: 'custo_total_projeto',
      type: 'money',
      placeholder: 'Valor Total',
      tress: true,
      required: false,
      disabled: true,
    },
    {
      ref: 'quantidade_placas',
      type: 'number',
      placeholder: 'Quantidade de Módulos',
      tress: true,
      required: true,
      min: '0',
      step: '1',
      onChange: handleCalculoPotencia,
    },
    {
      ref: 'marca_placas',
      placeholder: 'Marca dos Módulos',
      options: marcasMod,
      tress: true,
      required: true,
    },
    {
      ref: 'potencia_modulo',
      type: 'number',
      placeholder: 'Potência W dos Módulos',
      tress: true,
      required: true,
      min: '0',
      step: '0.01',
      onChange: handleCalculoPotencia,
    },
    {
      ref: 'potencia_kWp',
      type: 'number',
      placeholder: 'Potência kWp Total',
      tress: true,
      required: false,
      disabled: true,
    },
    {
      ref: 'quantidade_inversores',
      type: 'number',
      placeholder: 'Quantidade de Inversores',
      tress: true,
      required: true,
    },
    {
      ref: 'marca_inversores',
      placeholder: 'Marca dos Inversores',
      options: marcasInv,
      tress: true,
      required: true,
    },
    {
      ref: 'potencia_kW_inversor',
      type: 'number',
      placeholder: 'Potência kW Total',
      tress: true,
      required: true,
    },
    {
      ref: 'geracao_energia_estimada',
      type: 'number',
      placeholder: 'Geração de Energia kW/h no Primeiro Ano',
      required: true,
      tress: true,
    },
    {
      ref: 'concessionaria',
      placeholder: 'Concessionária de Energia',
      required: true,
      tress: true,
    },
    {
      ref: 'unidade_consumidora',
      placeholder: 'Unidade Consumidora',
      required: true,
      tress: true,
    },
    {
      ref: 'cod_proposta_externa',
      placeholder: 'Número da Proposta Comercial',
      required: false,
      tress: true,
    },
  ];

  const formInstallationAddress = [
    {
      ref: 'cep',
      placeholder: 'CEP',
      mask: '99999-999',
      tress: true,
      required: true,
      onBlur: handleInstallationAddress,
    },
    { ref: 'rua', placeholder: 'Logradouro', tress: true, required: true },
    {
      ref: 'numero',
      placeholder: 'Número',
      tress: true,
      required: true,
      type: 'number',
    },
    {
      ref: 'complemento',
      placeholder: 'Complemento',
      tress: true,
      required: false,
    },
    { ref: 'bairro', placeholder: 'Bairro', tress: true, required: true },
    { ref: 'cidade', placeholder: 'Cidade', tress: true, required: true },
    { ref: 'estado', placeholder: 'Estado', tress: true, required: true },
  ];

  const formCliente = [
    {
      ref: 'tcc2',
      placeholder: 'Tipo de Pessoa',
      options: pessoFisJudi,
      required: true,
      tress: true,
      onBlur: async () => {
        const value = refFormCliente?.current?.getItem('tcc2');
        setTipoCliente(value);
      },
    },
    {
      ref: 'nome_completo',
      placeholder: 'Nome Completo',
      required: true,
      tress: true,
    },
    {
      ref: 'cpf',
      placeholder: 'CPF/CNPJ',
      type: 'cpf',
      required: true,
      tress: true,
    },
    {
      ref: 'data_nascimento',
      type: 'date',
      placeholder:
        tipoCliente === 'juridica' ? 'Data abertura' : 'Data de Nascimento',
      required: true,
      tress: true,
    },
    { ref: 'email', placeholder: 'E-mail', required: true, tress: true },
    {
      ref: 'telefone_celular',
      placeholder: 'Telefone Celular',
      mask: '(99)99999-9999',
      required: true,
      tress: true,
      Tooltip: 'Preencha com números válidos — indispensável para comunicação.',
    },
    {
      ref: 'telefone_fixo',
      placeholder: 'Telefone Fixo',
      mask: '(99)9999-9999',
      required: false,
      tress: true,
      Tooltip: 'Preencha com números válidos — indispensável para comunicação.',
    },
    {
      ref: 'estado_civil',
      placeholder: 'Estado Civil',
      tress: true,
      required: true,
      options: esadoCivilOptions,
    },
    {
      ref: 'nome_mae',
      placeholder: 'Nome Completo da Mãe',
      required: true,
      tress: true,
    },

    ...(isBanking
      ? [
          {
            ref: 'rg',
            placeholder: 'RG',
            type: 'number',
            required: true,
            mask: '99.999.999-99',
            tress: true,
          },
          {
            ref: 'rg_data_emissao',
            placeholder: 'Data de Emissão',
            type: 'date',
            required: true,
            tress: true,
          },
          {
            ref: 'rg_emissor',
            placeholder: 'Orgão Emissor',
            required: true,
            tress: true,
            options: optionsOrgaoEmissorRg,
          },
          {
            ref: 'rg_emissor_uf',
            placeholder: 'UF de Emissão',
            required: true,
            tress: true,
            options: optionsStates,
          },
        ]
      : [
          {
            ref: 'rg',
            placeholder: 'RG',
            type: 'number',
            required: false,
            mask: '99.999.999-99',
            tress: true,
          },
        ]),
    {
      ref: 'genero',
      placeholder: 'Sexo',
      options: genderOptions,
      tress: true,
      required: true,
    },
    {
      ref: 'nacionalidade',
      placeholder: 'Nacionalidade',
      tress: true,
      required: true,
      options: nacionalidadeOptions,
    },
    {
      ref: 'cep_cliente',
      placeholder: 'CEP',
      mask: '99999-999',
      required: true,
      tress: true,
      onBlur: handleGetAddressByCep,
    },
    {
      ref: 'logradouro_cliente',
      placeholder: 'Logradouro',
      tress: true,
      required: true,
      // onBlur: () =>
      //   refFormAddressInstall?.current?.setItem(
      //     refFormCliente?.current?.getItem('logradouro_cliente'),
      //     'rua'
      //   ),
    },
    {
      ref: 'numero_cliente',
      placeholder: 'Número',
      tress: true,
      required: true,
      type: 'number',
    },
    {
      ref: 'complemento',
      placeholder: 'Complemento',
      tress: true,
      required: false,
      // onBlur: () =>
      //   refFormAddressInstall?.current?.setItem(
      //     refFormCliente?.current?.getItem('complemento'),
      //     'complemento'
      //   ),
    },
    {
      ref: 'bairro',
      placeholder: 'Bairro',
      tress: true,
      required: true,
      // onBlur: () =>
      //   refFormAddressInstall?.current?.setItem(
      //     refFormCliente?.current?.getItem('bairro'),
      //     'bairro'
      //   ),
    },
    {
      ref: 'cidade',
      placeholder: 'Cidade',
      tress: true,
      required: true,
      // onBlur: () =>
      //   refFormAddressInstall?.current?.setItem(
      //     refFormCliente?.current?.getItem('cidade'),
      //     'cidade'
      //   ),
    },
    {
      ref: 'estado',
      placeholder: 'Estado',
      tress: true,
      required: true,
      // onBlur: () =>
      //   refFormAddressInstall?.current?.setItem(
      //     refFormCliente?.current?.getItem('estado'),
      //     'estado'
      //   ),
    },
    {
      ref: 'id_pre_proposta',
      placeholder: 'ID Pré-Proposta',
      required: false,
      disabled: true,
    },
  ];

  const formProfessional = [
    { ref: 'profissao', placeholder: 'Profissão', required: true, tress: true },
    { ref: 'ocupacao', placeholder: 'Ocupação', required: true, tress: true },
    {
      ref: 'nome_empresa',
      placeholder: 'Nome da Empresa',
      required: true,
      tress: true,
    },
    {
      ref: 'telefone_empresa',
      placeholder: 'Telefone da empresa',
      mask: '(99)99999-9999',
      required: true,
      tress: true,
    },
    {
      ref: 'tempo_empresa',
      placeholder: 'Tempo de Empresa (Meses)',
      type: 'number',
      required: true,
      min: 0,
      tress: true,
    },
    {
      ref: 'renda_mensal',
      placeholder: 'Renda Mensal',
      type: 'money',
      required: true,
      tress: true,
    },
    {
      ref: 'patrimonio_estimado',
      placeholder: 'Patrimônio Estimado',
      type: 'money',
      required: true,
      tress: true,
    },
    {
      ref: 'situacao_imovel',
      placeholder: 'Situação do Imóvel',
      required: true,
      options: situacOptions,
      tress: true,
    },
  ];

  const save = async (e) => {
    e.preventDefault();
    if (loading) {
      return;
    }
    setLoading(true);
    const form = refForm?.current?.getForm();
    const formAddressInstall = refFormInstallationAddress?.current?.getForm();
    const formCliente = refFormCliente?.current?.getForm();
    const formProfessional = isBanking
      ? professionalData
      : refFormProfessional?.current?.getForm();
    let isProfessionalValid = false;
    if (isBanking) {
      isProfessionalValid = !!(
        formProfessional?.profissao &&
        formProfessional?.ocupacao &&
        formProfessional?.renda_mensal &&
        formProfessional?.nome_empresa
      );
    } else {
      isProfessionalValid = !!formProfessional;
    }

    if (!form || !formAddressInstall || !formCliente || !isProfessionalValid) {
      setLoading(false);
      toast.error('Todos os campos devem ser preenchidos');
      return;
    }
    const telefoneIntegrador = unmaskPhone(form?.telefone_integrador);
    const telefoneVendedor = unmaskPhone(form?.telefone_vendedor);
    const telefoneCliente = unmaskPhone(formCliente?.telefone_celular);
    const telefoneEmpresa = unmaskPhone(formProfessional?.telefone_empresa);

    if (
      telefoneCliente &&
      (telefoneCliente === telefoneIntegrador ||
        telefoneCliente === telefoneVendedor)
    ) {
      toast.error(
        'O Telefone Celular do Cliente não pode ser o mesmo do Integrador ou do Vendedor.'
      );
      setLoading(false);
      return;
    }

    //verifica se o nome da mae do cliente esta completo (pelo menos 2 palavras)
    if (formCliente?.nome_mae.trim().split(' ').length < 2) {
      toast.error('O nome da mãe do cliente deve ter nome e sobrenome.');
      setLoading(false);
      return;
    }

    const viaCEP = await ReadAddressesByZipCode(formAddressInstall.cep);

    const payload = {
      data: {
        distribuidor: distributorId,
        situacao_imovel: formProfessional?.situacao_imovel,
        pre_proposal: formCliente?.id_pre_proposta,
        cod_proposta_externa: normalizeName(form?.cod_proposta_externa),
        telefone_integrador: telefoneIntegrador,
        informacoes_projeto: {
          custo_produto:
            form?.custo_produto + (form?.valor_outros_produtos || 0),
          custo_servico: form?.custo_servico || 0,
          custo_total_projeto: form?.custo_total_projeto,
          quantidade_placas: form?.quantidade_placas,
          marca_placas: form?.marca_placas,
          potencia_W_placas: form?.potencia_modulo,
          quantidade_inversores: form?.quantidade_inversores,
          potencia_kW_inversor: form?.potencia_kW_inversor,
          potencia_kWp: form?.potencia_kWp,
          marca_inversores: form?.marca_inversores,
          geracao_energia_estimada: parseInt(
            form?.geracao_energia_estimada?.replace(/\D/g, '')
          ),
          cep_instalacao: formAddressInstall?.cep,
          concessionaria: form?.concessionaria,
          unidade_consumidora: form?.unidade_consumidora,
          nome_vendedor: form?.nome_vendedor.trim(),
          telefone_vendedor: telefoneVendedor,
          empresa_vendedor_cnpj: form?.cnpj_integrador,
        },
        endereco_instalacao: {
          rua: formAddressInstall?.rua,
          numero: formAddressInstall?.numero,
          cidade: viaCEP?.localidade || formAddressInstall?.cidade,
          uf: viaCEP?.uf || formAddressInstall?.estado,
          bairro: viaCEP?.bairro || formAddressInstall?.bairro,
          complemento: formAddressInstall?.complemento,
          cep: formAddressInstall?.cep,
        },
        endereco_correspondencia: {
          rua: formAddressInstall?.rua,
          numero: formAddressInstall?.numero,
          cidade: viaCEP?.localidade || formAddressInstall?.cidade,
          uf: viaCEP?.uf || formAddressInstall?.estado,
          bairro: viaCEP?.bairro || formAddressInstall?.bairro,
          complemento: formAddressInstall?.complemento,
          cep: formAddressInstall?.cep,
        },
        endereco_residencial: {
          rua: formAddressInstall?.rua,
          numero: formAddressInstall?.numero,
          cidade: viaCEP?.localidade || formAddressInstall?.cidade,
          uf: viaCEP?.uf || formAddressInstall?.estado,
          bairro: viaCEP?.bairro || formAddressInstall?.bairro,
          complemento: formAddressInstall?.complemento,
          cep: formAddressInstall?.cep,
        },
        informacoes_profissionais: {
          profissao: isBanking
            ? formProfessional?.profissao_label
            : formProfessional?.profissao?.trim(),
          profissao_code: isBanking ? formProfessional?.profissao : null,
          ocupacao: isBanking
            ? formProfessional?.ocupacao_label
            : formProfessional?.ocupacao?.trim(),
          ocupacao_code: isBanking ? formProfessional?.ocupacao : null,
          tempo_empresa: formProfessional?.tempo_empresa,
          cnpj_empresa: formProfessional?.cnpj_empresa ?? '',
          nome_empresa: formProfessional?.nome_empresa,
          telefone_empresa: telefoneEmpresa,
          renda_mensal: formProfessional?.renda_mensal,
          patrimonio_estimado: formProfessional?.patrimonio_estimado,
        },
        informacoes_cliente: {
          tipo_cliente: formCliente?.tcc2,
          [formCliente?.cpf.length === 14 ? 'cpf' : 'cnpj']: formCliente?.cpf,
          data_nascimento: formCliente?.data_nascimento,
          email: formCliente?.email,
          telefone_celular: unmaskPhone(formCliente?.telefone_celular),
          telefone_fixo: unmaskPhone(formCliente?.telefone_fixo),
          nome_completo: formCliente?.nome_completo.trim(),
          estado_civil: formCliente?.estado_civil,
          nome_mae: formCliente?.nome_mae.trim(),
          rg: formCliente?.rg?.replace(/\D/g, ''),
          rg_data_emissao: isBanking ? formCliente?.rg_data_emissao : null,
          rg_emissor: isBanking ? formCliente?.rg_emissor : null,
          rg_emissor_uf: isBanking ? formCliente?.rg_emissor_uf : null,
          genero: formCliente?.genero,
          nacionalidade: formCliente?.nacionalidade,
        },
      },
    };

    try {
      const result = await POST(saveUrl, payload);
      if (result && !exposeStrapiError(result)) {
        toast.success('Proposta enviada com sucesso!');
        setConfirmDisplay('200');
      } else throw new Error('Erro ao enviar proposta');
    } catch (err) {
      console.error('Erro ao enviar proposta:', err);
      toast.error(
        'Ocorreu um erro ao enviar a proposta. Verifique os dados e tente novamente.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemedComponent>
      {confirmDisplay === '200' ? (
        <EproposalConfirm isMobile={isMobile} logoURL={logoURL} />
      ) : confirmDisplay === '400' ? (
        <EProposalInvalidToken />
      ) : (
        <>
          <EproposalFormsWrapper>
            {logoURL && (
              <div
                style={
                  isMobile
                    ? {
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }
                    : {}
                }
              >
                <img
                  src={logoURL}
                  alt="Logo"
                  style={
                    isMobile
                      ? { maxHeight: '3rem' }
                      : {
                          position: 'absolute',
                          maxHeight: '4rem',
                          left: '2rem',
                        }
                  }
                />
              </div>
            )}
            <Title centred={true}>Proposta</Title>
            <EproposalContainer>
              <HeaderButtonContainer>
                <Title small nomargin>
                  Informações do Cliente
                </Title>
              </HeaderButtonContainer>
              <FormCore ref={refFormCliente} formItems={formCliente} />
              <FormSpacing />
              <Title small nomargin>
                Endereço de Instalação
              </Title>
              <FormCore
                ref={refFormInstallationAddress}
                formItems={formInstallationAddress}
              />
              <FormSpacing />
              <Title small nomargin>
                Informações do Projeto
              </Title>
              <FormCore ref={refForm} formItems={formProjectItems} />
              <FormSpacing />
              <Title small nomargin>
                Informações Profissionais
              </Title>
              {isBanking ? (
                <ButtonContainer style={{ marginTop: '10px' }}>
                  <Button
                    color={professionalDescription ? 'green' : 'blue'}
                    width={'fit-content'}
                    small
                    nospace
                    onClick={() =>
                      setModal({
                        type: 'profissao_omni',
                        dados: isBankingData,
                        classesOptions: classesOptions,
                        distributorId: distributorId,
                        onConfirm: (dadosCompletos) => {
                          setProfessionalData(dadosCompletos);

                          refFormProfessional.current?.setValues(
                            dadosCompletos
                          );
                          setProfessionalDescription(
                            dadosCompletos.ocupacao_label
                          );
                        },
                      })
                    }
                  >
                    {professionalDescription
                      ? `✅ Profissão: ${professionalDescription}`
                      : '⚠️ Clique aqui para preencher Info. Profissionais'}
                  </Button>
                </ButtonContainer>
              ) : (
                <FormCore
                  ref={refFormProfessional}
                  formItems={formProfessional}
                />
              )}
              <FormSpacing />
              <ButtonContainer>
                <Button
                  type="submit"
                  color="blue"
                  width={'fit-content'}
                  small
                  nospace
                  loading={loading}
                  disabled={loading}
                  onClick={save}
                >
                  Enviar Proposta
                </Button>
              </ButtonContainer>
            </EproposalContainer>
          </EproposalFormsWrapper>
        </>
      )}
    </ThemedComponent>
  );
}
